import { createSlice } from '@reduxjs/toolkit';

const initialState = {
    logs: [],
    totalAudioFiles: 0,
    totalWavFiles: 0,
    incompatibleFilesFound: 0,
    fixedFiles: 0,
    dependsFilesFound: 0,
    // Counted explicitly by the backend — NOT derived as total minus the
    // others, which made unprocessed files render as "Compatible" mid-run.
    compatibleFilesFound: 0,
    convertedFilesFound: 0
};

const logsSlice = createSlice({
    name: 'logs',
    initialState,
    reducers: {
        addLogs(state, action) {
            if (Array.isArray(action.payload) && action.payload.length > 0) {
                state.logs = [...action.payload, ...state.logs];
            }
        },
        updateOrAddLogs(state, action) {
            if (!Array.isArray(action.payload) || action.payload.length === 0) {
                return;
            }

            // Build a map of existing logs by filePath for fast lookup
            const existingLogsMap = new Map();
            const logsWithoutPath = [];

            state.logs.forEach((log, index) => {
                if (log && typeof log === 'object' && log.filePath) {
                    existingLogsMap.set(log.filePath, index);
                } else {
                    logsWithoutPath.push(log);
                }
            });

            // Process new logs
            const newLogsToAdd = [];
            // Rows added by THIS call, by path — a second entry for the same
            // new file in one batch (ticks are batched, see
            // utils/progressBatcher.js) must merge into the row queued above,
            // not be indexed as if it were already in state.logs.
            const pendingNew = new Map();
            const indicesToDrop = new Set();
            action.payload.forEach(newLog => {
                // Handle separators, summaries, and entries without filePath - always add
                if (!newLog || typeof newLog !== 'object' || !newLog.filePath ||
                    newLog.status === 'separator' || newLog.status === 'summary') {
                    newLogsToAdd.push(newLog);
                    return;
                }

                const queued = pendingNew.get(newLog.filePath);
                if (queued !== undefined) {
                    newLogsToAdd[queued] = { ...newLogsToAdd[queued], ...newLog };
                    return;
                }
                const existingIndex = existingLogsMap.get(newLog.filePath);
                // A log carrying oldFilePath is a produced file (conversion /
                // "[fixed]" copy) — it REPLACES the original's row at its
                // position, so a fix updates rows in place instead of the row
                // vanishing and a new one appearing at the top.
                const oldIndex = (newLog.oldFilePath && newLog.oldFilePath !== newLog.filePath)
                    ? existingLogsMap.get(newLog.oldFilePath)
                    : undefined;

                if (existingIndex !== undefined) {
                    // Update existing log
                    state.logs[existingIndex] = { ...state.logs[existingIndex], ...newLog };
                    // The original's row is superseded by the produced file's —
                    // keeping both would show the same track twice.
                    if (oldIndex !== undefined && oldIndex !== existingIndex) indicesToDrop.add(oldIndex);
                } else if (oldIndex !== undefined) {
                    state.logs[oldIndex] = { ...state.logs[oldIndex], ...newLog };
                    existingLogsMap.delete(newLog.oldFilePath);
                    existingLogsMap.set(newLog.filePath, oldIndex);
                } else {
                    // Add as new log (prepended below — so it is NOT
                    // addressable through existingLogsMap's state indices)
                    newLogsToAdd.push(newLog);
                    pendingNew.set(newLog.filePath, newLogsToAdd.length - 1);
                }
            });

            if (indicesToDrop.size > 0) {
                state.logs = state.logs.filter((_, i) => !indicesToDrop.has(i));
            }
            // Add new logs to the beginning
            if (newLogsToAdd.length > 0) {
                state.logs = [...newLogsToAdd, ...state.logs];
            }
        },
        clearLogs(state) {
            state.logs = [];
        },
        // Whole-slice reset for account switch: the rows carry the previous
        // account's track names and the counters describe their run, so
        // clearLogs alone would leave the tallies behind.
        resetLogsState() {
            return initialState;
        },
        setLogs(state, action) {
            state.logs = action.payload;
        },
        // Drop rows still waiting on a result. Pre-populated "pending" rows
        // that the run never reported on (skipped by the walker, cancelled,
        // or extension-set mismatch) must not linger as forever-queued.
        clearPendingLogs(state) {
            state.logs = state.logs.filter((l) => !l || typeof l !== 'object' || l.status !== 'pending');
        },
        // Remove ONE track's rows from the list (the row menu's "Remove from list").
        removeLogsForFile(state, action) {
            state.logs = state.logs.filter((l) => !l || l.filePath !== action.payload);
        },
        addLogsLineBreak(state) {
            state.logs = ['\n', ...state.logs];
        },
        setTotalAudioFiles(state, action) {
            state.totalAudioFiles = action.payload;
        },
        setTotalWavFiles(state, action) {
            state.totalWavFiles = action.payload;
        },
        setIncompatibleFilesFound(state, action) {
            state.incompatibleFilesFound = action.payload;
        },
        setFixedFiles(state, action) {
            state.fixedFiles = action.payload;
        },
        setDependsFilesFound(state, action) {
            state.dependsFilesFound = action.payload;
        },
        setCompatibleFilesFound(state, action) {
            state.compatibleFilesFound = action.payload;
        },
        setConvertedFilesFound(state, action) {
            state.convertedFilesFound = action.payload;
        }
    },
});

export const {
    addLogs,
    updateOrAddLogs,
    clearLogs, setLogs,
    resetLogsState,
    clearPendingLogs,
    removeLogsForFile,
    addLogsLineBreak,
    setFixedFiles,
    setIncompatibleFilesFound,
    setTotalAudioFiles,
    setTotalWavFiles,
    setDependsFilesFound,
    setCompatibleFilesFound,
    setConvertedFilesFound
} = logsSlice.actions;
export default logsSlice.reducer;
